import React, { useEffect, useState } from "react";
import { Icon } from "./icons";
import "./ManageSchemes.css";

/* ✅ CLOUD BACKEND */
const API =
  process.env.REACT_APP_API_URL ||
  "https://backend-9i6n.onrender.com";

const ManageSchemes = () => {
  const [schemes, setSchemes] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success"); // success or error

  const [form, setForm] = useState({
    title: "",
    description: "",
    benefits: "",
    eligibility: "",
    applyProcess: "",
    applyLink: "",
    schemeType: "Central",
  });

  const token = localStorage.getItem("token");

  /* ================= SHOW POPUP ================= */
  const displayPopup = (message, type = "success") => {
    setPopupMessage(message);
    setPopupType(type);
    setShowPopup(true);

    setTimeout(() => {
      setShowPopup(false);
    }, 2500);
  };

  /* ================= FETCH ================= */
  const fetchSchemes = async () => {
    try {
      const res = await fetch(`${API}/api/schemes`);
      const data = await res.json();
      setSchemes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch schemes error:", err);
      displayPopup("योजना लोड करता आल्या नाहीत", "error");
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  /* ================= INPUT ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ================= ADD / UPDATE ================= */
  const submitScheme = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const url = editingId
      ? `${API}/api/schemes/${editingId}`
      : `${API}/api/schemes`;

    const method = editingId ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        const message = editingId
          ? "योजना यशस्वीरित्या अद्ययावत केली!"
          : "योजना यशस्वीरित्या जोडली!";
        displayPopup(message, "success");
        resetForm();
        fetchSchemes();
      } else {
        displayPopup("योजना जतन करता आली नाही", "error");
      }
    } catch (err) {
      console.error("Submit scheme error:", err);
      displayPopup("एक त्रुटी आली. कृपया पुन्हा प्रयत्न करा.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= EDIT ================= */
  const startEdit = (scheme) => {
    setEditingId(scheme._id);
    setForm({
      title: scheme.title,
      description: scheme.description || "",
      benefits: scheme.benefits || "",
      eligibility: scheme.eligibility || "",
      applyProcess: scheme.applyProcess || "",
      applyLink: scheme.applyLink || "",
      schemeType: scheme.schemeType || "Central",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= DELETE ================= */
  const deleteScheme = async (id) => {
    if (!window.confirm("ही योजना नक्की हटवायची आहे का?")) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/api/schemes/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        displayPopup("योजना यशस्वीरित्या हटवली!", "success");
        fetchSchemes();
      } else {
        displayPopup("योजना हटवता आली नाही", "error");
      }
    } catch (err) {
      console.error("Delete scheme error:", err);
      displayPopup("हटवताना त्रुटी आली", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= RESET ================= */
  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      description: "",
      benefits: "",
      eligibility: "",
      applyProcess: "",
      applyLink: "",
      schemeType: "Central",
    });
  };

  /* ================= FILTER (client-side only) ================= */
  const shown = schemes
    .filter((s) => (typeFilter === "all" ? true : s.schemeType === typeFilter))
    .filter((s) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        s.title?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    });

  return (
    <div className="admin-page">
      {/* TOAST */}
      {showPopup && (
        <div className={`gp-toast gp-toast-${popupType === "error" ? "error" : "success"}`}>
          <span>{popupType === "success" ? "✅" : "❌"}</span>
          <p style={{ margin: 0 }}>{popupMessage}</p>
        </div>
      )}

      {/* HEADER */}
      <div className="gp-page-head">
        <div>
          <h1>शासकीय योजना</h1>
          <p>गावासाठी उपलब्ध शासकीय योजना व लाभार्थी माहिती व्यवस्थापित करा</p>
        </div>
      </div>

      {/* FORM */}
      <div className="gp-card scheme-form-card">
        <h3 className="scheme-form-title">
          {editingId ? "योजना संपादित करा" : "नवीन योजना जोडा"}
        </h3>

        <form className="scheme-form-grid" onSubmit={submitScheme}>
          <input
            className="gp-input"
            name="title"
            placeholder="शीर्षक"
            value={form.title}
            onChange={handleChange}
            required
            disabled={isLoading}
          />

          <textarea
            className="gp-textarea"
            name="description"
            placeholder="वर्णन"
            value={form.description}
            onChange={handleChange}
            disabled={isLoading}
          />

          <textarea
            className="gp-textarea"
            name="benefits"
            placeholder="लाभ"
            value={form.benefits}
            onChange={handleChange}
            disabled={isLoading}
          />

          <textarea
            className="gp-textarea"
            name="eligibility"
            placeholder="पात्रता"
            value={form.eligibility}
            onChange={handleChange}
            disabled={isLoading}
          />

          <textarea
            className="gp-textarea"
            name="applyProcess"
            placeholder="अर्ज प्रक्रिया"
            value={form.applyProcess}
            onChange={handleChange}
            disabled={isLoading}
          />

          <input
            className="gp-input"
            type="url"
            name="applyLink"
            placeholder="अर्ज लिंक (https://...)"
            value={form.applyLink}
            onChange={handleChange}
            disabled={isLoading}
          />

          <select
            className="gp-select"
            name="schemeType"
            value={form.schemeType}
            onChange={handleChange}
            disabled={isLoading}
          >
            <option value="Central">केंद्र सरकार</option>
            <option value="State">राज्य सरकार</option>
          </select>

          <div className="scheme-form-actions">
            <button type="submit" className="gp-btn gp-btn-green" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="gp-spinner" /> {editingId ? "अद्ययावत करत आहे..." : "जोडत आहे..."}
                </>
              ) : (
                <>
                  <Icon.plus size={16} /> {editingId ? "योजना अद्ययावत करा" : "योजना जोडा"}
                </>
              )}
            </button>

            {editingId && (
              <button
                type="button"
                className="gp-btn gp-btn-ghost"
                onClick={resetForm}
                disabled={isLoading}
              >
                रद्द करा
              </button>
            )}
          </div>
        </form>
      </div>

      {/* TOOLBAR */}
      <div className="gp-toolbar" style={{ marginTop: 26 }}>
        <div className="gp-search-box">
          <Icon.search size={16} />
          <input
            placeholder="योजना शोधा…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="gp-chip-group">
          <button
            className={`gp-chip ${typeFilter === "all" ? "active" : ""}`}
            onClick={() => setTypeFilter("all")}
          >
            सर्व
          </button>
          <button
            className={`gp-chip ${typeFilter === "Central" ? "active" : ""}`}
            onClick={() => setTypeFilter("Central")}
          >
            केंद्र
          </button>
          <button
            className={`gp-chip ${typeFilter === "State" ? "active" : ""}`}
            onClick={() => setTypeFilter("State")}
          >
            राज्य
          </button>
        </div>
        <span className="gp-toolbar-count">एकूण {shown.length} योजना</span>
      </div>

      {/* LIST */}
      {schemes.length === 0 ? (
        <div className="gp-empty">कोणतीही योजना आढळली नाही. वरील फॉर्ममधून पहिली योजना जोडा!</div>
      ) : shown.length === 0 ? (
        <div className="gp-empty">या शोधासाठी कोणतीही योजना सापडली नाही</div>
      ) : (
        <div className="gp-record-grid">
          {shown.map((s) => (
            <div className="gp-record-card" key={s._id}>
              <div className="gp-record-top">
                <span className="gp-badge gp-badge-blue">
                  {s.schemeType === "State" ? "राज्य सरकार" : "केंद्र सरकार"}
                </span>
              </div>

              <h3 className="gp-record-title">{s.title}</h3>
              {s.description && <p className="gp-record-desc">{s.description}</p>}

              <div className="gp-record-foot">
                <span className="gp-record-date">
                  {s.createdAt && (
                    <>
                      <Icon.calendar size={14} />
                      {new Date(s.createdAt).toLocaleDateString("en-GB")}
                    </>
                  )}
                </span>
                <div className="gp-record-actions">
                  <button
                    className="gp-icon-btn"
                    title="संपादित करा"
                    onClick={() => startEdit(s)}
                    disabled={isLoading}
                  >
                    <Icon.edit size={15} />
                  </button>
                  <button
                    className="gp-icon-btn gp-icon-btn-danger"
                    title="हटवा"
                    onClick={() => deleteScheme(s._id)}
                    disabled={isLoading}
                  >
                    <Icon.trash size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageSchemes;
